import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesInsert, TablesUpdate } from "@/types/database.types";

// Core database types - use Tables<> as single source of truth
export type Plan = Tables<"plans">;
export type PlanFeatures = Tables<"plan_features">;
export type Organization = Tables<"organizations">;

// Extended types for API responses
export type PlanWithFeatures = Tables<"plans"> & {
  features: Tables<"plan_features">;
};

// Insert/Update types for admin operations
export type PlanInsert = TablesInsert<"plans">;
export type PlanUpdate = TablesUpdate<"plans">;
export type PlanFeaturesInsert = TablesInsert<"plan_features">;
export type PlanFeaturesUpdate = TablesUpdate<"plan_features">;

// Plan name to UUID mapping cache
let planMappingCache: Record<string, string> | null = null;

/**
 * Map plan names to UUIDs for backend compatibility
 * This allows frontend to use "starter", "pro" while backend uses UUIDs
 */
const PLAN_NAME_TO_UUID_MAP: Record<string, string> = {
  starter: "cbc50e07-e21b-42af-a3dd-9c1c8d9a6334",
  pro: "28d9a7cd-1733-4490-a79c-534eab14b093",
  business: "fb601a9a-f5d2-4ba0-999c-7f3c65c0ce43",
  "business+": "913eef09-6a21-41c3-98a9-dcb621d499cc",
};

/**
 * Get plan UUID from plan name
 */
export function getPlanUuidFromName(planName: string): string | null {
  const normalizedName = planName.toLowerCase();
  return PLAN_NAME_TO_UUID_MAP[normalizedName] || null;
}

/**
 * Get plan name from UUID
 */
export function getPlanNameFromUuid(uuid: string): string | null {
  const entry = Object.entries(PLAN_NAME_TO_UUID_MAP).find(
    ([_, id]) => id === uuid,
  );
  return entry ? entry[0] : null;
}

/**
 * Fetch all active plans with their features from the database
 */
export async function fetchPlans(): Promise<{
  data: PlanWithFeatures[] | null;
  error: Error | null;
}> {
  try {
    const { data: plansData, error: plansError } = await supabase
      .from("plans")
      .select(
        `
        *,
        plan_features!inner(*)
      `,
      )
      .eq("is_active", true)
      .order("monthly_price");

    if (plansError) {
      throw new Error(`Failed to fetch plans: ${plansError.message}`);
    }

    if (!plansData || plansData.length === 0) {
      return { data: [], error: null };
    }

    // Transform the data to match our PlanWithFeatures type
    const plansWithFeatures: PlanWithFeatures[] = plansData.map((plan) => ({
      ...plan,
      features: plan.plan_features[0], // Since we use inner join, we know there's exactly one feature set per plan
    }));

    return { data: plansWithFeatures, error: null };
  } catch (error) {
    console.error("Error fetching plans:", error);
    return {
      data: null,
      error:
        error instanceof Error
          ? error
          : new Error("Unknown error fetching plans"),
    };
  }
}

/**
 * Fetch a specific plan by name (e.g., "starter", "pro")
 */
export async function fetchPlanByName(planName: string): Promise<{
  data: PlanWithFeatures | null;
  error: Error | null;
}> {
  try {
    const planUuid = getPlanUuidFromName(planName);
    if (!planUuid) {
      throw new Error(`Invalid plan name: ${planName}`);
    }

    const { data: planData, error: planError } = await supabase
      .from("plans")
      .select(
        `
        *,
        plan_features!inner(*)
      `,
      )
      .eq("id", planUuid)
      .eq("is_active", true)
      .single();

    if (planError) {
      throw new Error(`Failed to fetch plan: ${planError.message}`);
    }

    if (!planData) {
      return { data: null, error: null };
    }

    const planWithFeatures: PlanWithFeatures = {
      ...planData,
      features: planData.plan_features[0],
    };

    return { data: planWithFeatures, error: null };
  } catch (error) {
    console.error(`Error fetching plan ${planName}:`, error);
    return {
      data: null,
      error:
        error instanceof Error
          ? error
          : new Error(`Unknown error fetching plan ${planName}`),
    };
  }
}

/**
 * Build the mapping cache from database
 */
export async function buildPlanMappingCache(): Promise<void> {
  try {
    const { data: plans, error } = await supabase
      .from("plans")
      .select("id, name")
      .eq("is_active", true);

    if (error || !plans) {
      console.error("Failed to build plan mapping cache:", error);
      return;
    }

    planMappingCache = {};
    plans.forEach((plan) => {
      planMappingCache![plan.name.toLowerCase()] = plan.id;
    });
  } catch (error) {
    console.error("Error building plan mapping cache:", error);
  }
}

/**
 * Get formatted features list for display
 */
export function getFormattedFeatures(plan: PlanWithFeatures): string[] {
  const features: string[] = [];

  // Basic features
  if (plan.features.max_active_agents) {
    features.push(
      `${plan.features.max_active_agents} AI agent${plan.features.max_active_agents > 1 ? "s" : ""}`,
    );
  }

  if (plan.features.max_conversations_per_month) {
    features.push(
      `${plan.features.max_conversations_per_month.toLocaleString()} chat messages/month`,
    );
  }

  // Voice features
  if (
    plan.features.has_voice_agents &&
    plan.features.max_voice_minutes_per_month
  ) {
    features.push(`${plan.features.max_voice_minutes_per_month} voice minutes`);
    if (plan.features.voice_overage_cost_per_minute) {
      features.push(
        `$${plan.features.voice_overage_cost_per_minute}/min overage`,
      );
    }
  } else if (!plan.features.has_voice_agents) {
    features.push("No voice agents");
  }

  // Storage and knowledge
  if (plan.features.max_knowledge_items) {
    features.push(`${plan.features.max_knowledge_items} knowledge sources`);
  }

  if (plan.features.max_storage_mb) {
    const storageGB =
      plan.features.max_storage_mb >= 1000
        ? `${plan.features.max_storage_mb / 1000}GB`
        : `${plan.features.max_storage_mb}MB`;
    features.push(`${storageGB} storage`);
  }

  return features;
}

/**
 * Get additional features for display (things like "Everything in Starter plus:")
 */
export function getAdditionalFeatures(plan: PlanWithFeatures): string[] {
  const additionalFeatures: string[] = [];

  if (plan.features.has_voice_agents) {
    additionalFeatures.push("Voice agents with smart call transfer");
  }

  if (plan.features.can_multi_language) {
    additionalFeatures.push("Multi language support (10+)");
  }

  if (plan.features.can_web_search) {
    additionalFeatures.push("Live Web & Google access");
  }

  if (plan.features.can_remove_powered_by) {
    additionalFeatures.push('Remove "Powered by" branding');
  }

  if (plan.features.can_custom_domain) {
    additionalFeatures.push("Custom domain support");
  }

  if (plan.features.has_custom_onboarding) {
    additionalFeatures.push("Dedicated account manager");
  }

  return additionalFeatures;
}

// ============================================================================
// ADMIN PLAN MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Fetch all plans (including inactive ones) for admin use
 */
export async function fetchAllPlansForAdmin(): Promise<{
  data: PlanWithFeatures[] | null;
  error: Error | null;
}> {
  try {
    const { data: plansData, error: plansError } = await supabase
      .from("plans")
      .select("*")
      .order("monthly_price");

    if (plansError) {
      throw new Error(`Failed to fetch plans: ${plansError.message}`);
    }

    if (!plansData || plansData.length === 0) {
      return { data: [], error: null };
    }

    // Fetch plan features separately to avoid multiple rows issue
    const { data: allFeaturesData, error: featuresError } = await supabase
      .from("plan_features")
      .select("*");

    if (featuresError) {
      throw new Error(
        `Failed to fetch plan features: ${featuresError.message}`,
      );
    }

    // Create a map of plan_id to features for easy lookup
    const featuresMap = new Map();
    allFeaturesData?.forEach((feature) => {
      featuresMap.set(feature.plan_id, feature);
    });

    // Transform the data to match our PlanWithFeatures type
    const plans: PlanWithFeatures[] = plansData.map((plan) => ({
      ...plan,
      features: featuresMap.get(plan.id) || {}, // Get features for this plan
    }));

    return { data: plans, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.error("Error fetching plans for admin:", errorMessage);
    return { data: null, error: new Error(errorMessage) };
  }
}

/**
 * Update a plan's basic information using admin edge function
 */
export async function updatePlan(
  planId: string,
  updates: Partial<PlanUpdate>,
): Promise<{
  data: Plan | null;
  error: Error | null;
}> {
  try {
    // Remove fields that shouldn't be updated directly
    const { id, created_at, updated_at, ...safeUpdates } = updates as any;

    const { data, error } = await supabase.functions.invoke(
      "admin-update-plan",
      {
        body: {
          planId,
          planUpdates: safeUpdates,
        },
      },
    );

    if (error) {
      throw new Error(`Failed to update plan: ${error.message}`);
    }

    if (!data?.success) {
      throw new Error("Failed to update plan: Invalid response");
    }

    return { data: data.data, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.error("Error updating plan:", errorMessage);
    return { data: null, error: new Error(errorMessage) };
  }
}

/**
 * Update a plan's features
 */
export async function updatePlanFeatures(
  planId: string,
  features: Partial<PlanFeaturesUpdate>,
): Promise<{
  data: PlanWithFeatures | null;
  error: Error | null;
}> {
  try {
    // Remove fields that shouldn't be updated directly
    const { id, plan_id, created_at, updated_at, ...safeFeatures } =
      features as any;

    const { data, error } = await supabase.functions.invoke(
      "admin-update-plan",
      {
        body: {
          planId,
          featuresUpdates: safeFeatures,
        },
      },
    );

    if (error) {
      throw new Error(`Failed to update plan features: ${error.message}`);
    }

    if (!data?.success) {
      throw new Error("Failed to update plan features: Invalid response");
    }

    return { data: data.data, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.error("Error updating plan features:", errorMessage);
    return { data: null, error: new Error(errorMessage) };
  }
}

/**
 * Create a new plan with features
 */
export async function createPlan(
  planData: PlanInsert,
  features: PlanFeaturesInsert,
): Promise<{
  data: PlanWithFeatures | null;
  error: Error | null;
}> {
  try {
    // First create the plan
    const { data: newPlan, error: planError } = await supabase
      .from("plans")
      .insert(planData)
      .select()
      .single();

    if (planError || !newPlan) {
      throw new Error(`Failed to create plan: ${planError?.message}`);
    }

    // Then create the features
    const { data: newFeatures, error: featuresError } = await supabase
      .from("plan_features")
      .insert({
        ...features,
        plan_id: newPlan.id,
      })
      .select()
      .single();

    if (featuresError) {
      // Rollback plan creation if features fail
      await supabase.from("plans").delete().eq("id", newPlan.id);
      throw new Error(
        `Failed to create plan features: ${featuresError.message}`,
      );
    }

    // Return the complete plan
    const plan: PlanWithFeatures = {
      ...newPlan,
      features: newFeatures,
    };

    return { data: plan, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.error("Error creating plan:", errorMessage);
    return { data: null, error: new Error(errorMessage) };
  }
}

/**
 * Delete a plan and its features
 */
export async function deletePlan(planId: string): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    // Check if any organizations are using this plan
    const { data: organizations, error: orgError } = await supabase
      .from("organizations")
      .select("id")
      .eq("plan_id", planId)
      .limit(1);

    if (orgError) {
      throw new Error(`Failed to check plan usage: ${orgError.message}`);
    }

    if (organizations && organizations.length > 0) {
      throw new Error("Cannot delete plan that is being used by organizations");
    }

    // Delete features first (due to foreign key constraint)
    const { error: featuresError } = await supabase
      .from("plan_features")
      .delete()
      .eq("plan_id", planId);

    if (featuresError) {
      throw new Error(
        `Failed to delete plan features: ${featuresError.message}`,
      );
    }

    // Then delete the plan
    const { error: planError } = await supabase
      .from("plans")
      .delete()
      .eq("id", planId);

    if (planError) {
      throw new Error(`Failed to delete plan: ${planError.message}`);
    }

    return { success: true, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.error("Error deleting plan:", errorMessage);
    return { success: false, error: new Error(errorMessage) };
  }
}

/**
 * Get shared features that all plans include
 */
export function getSharedFeatures(): string[] {
  return [
    "Chat branding (logo, colors)",
    "Collect information (fields)",
    "Conversation inbox + Email alerts",
    "Zapier / webhook integration",
    "Live access to your website",
  ];
}
